import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSetup, useUpdateSetup, type ChecklistItem, type SetupStatus } from '../../hooks/useSetup.ts';
import { INTEGRATIONS_PATH } from '../../lib/setup.ts';
import { HistoryImportButton } from './HistoryImport.tsx';
import { SETUP_CARD } from './styles.ts';

const ACTION =
  'shrink-0 px-3 py-1 rounded-lg text-xs font-semibold bg-(--color-accent)/15 text-(--color-accent) border border-(--color-accent)/30 hover:bg-(--color-accent)/25 transition-colors';

// Notifications are app-only (§4): the web never lists them.
const WEB_ITEMS = new Set(['connect', 'history', 'ftp']);

/**
 * "Finish setting up" card at the top of the Dashboard while
 * ``checklist.visible``: connect / history / FTP, each ticked or with its
 * action. "×" dismisses it for good.
 */
export function SetupChecklist() {
  const { t } = useTranslation();
  const { data } = useSetup();
  const update = useUpdateSetup();
  if (!data?.checklist.visible) return null;
  const items = data.checklist.items.filter((item) => WEB_ITEMS.has(item.id));
  return (
    <section aria-labelledby="setup-checklist-title" className={SETUP_CARD}>
      <div className="flex items-start justify-between gap-2">
        <h2 id="setup-checklist-title" className="text-base font-semibold text-(--color-text-primary)">
          {t('setup.checklist.title')}
        </h2>
        <button
          type="button"
          aria-label={t('setup.checklist.dismiss')}
          onClick={() => update.mutate({ checklist_dismissed: true })}
          disabled={update.isPending}
          className="text-(--color-text-secondary) hover:text-(--color-text-primary) px-1 text-lg leading-none"
        >
          ×
        </button>
      </div>
      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <ChecklistRow key={item.id} item={item} setup={data} />
        ))}
      </ul>
    </section>
  );
}

function ChecklistRow({ item, setup }: Readonly<{ item: ChecklistItem; setup: SetupStatus }>) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center justify-between gap-3 text-sm">
      <span className={item.done ? 'text-(--color-text-secondary) line-through' : 'text-(--color-text-primary)'}>
        {t(`setup.checklist.items.${item.id}`)}
      </span>
      {item.done ? (
        <span className="text-(--color-accent)">
          <span aria-hidden="true">✓</span>
          <span className="sr-only">{t('setup.checklist.done')}</span>
        </span>
      ) : (
        <ChecklistAction id={item.id} setup={setup} />
      )}
    </li>
  );
}

function ChecklistAction({ id, setup }: Readonly<{ id: ChecklistItem['id']; setup: SetupStatus }>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (id === 'history') {
    return (
      <HistoryImportButton zipImport={setup.history.zip_import} className={ACTION}>
        {t('setup.checklist.actions.history')}
      </HistoryImportButton>
    );
  }
  const target = id === 'connect' ? INTEGRATIONS_PATH : '/profile';
  return (
    <button type="button" onClick={() => navigate(target)} className={ACTION}>
      {t(`setup.checklist.actions.${id}`)}
    </button>
  );
}
